// Fills one chat message row - the skeleton Element::ChatMessageRowComponent renders into a
// <template> - so the live chat panel and chat replay render identical rows. Every slot is filled
// with createElement/setAttribute/textContent, never innerHTML: names and bodies are user input.
import { applyAvatar } from "utils/avatar";

// Matches only http(s):// URLs (bare domains and javascript:/data:/mailto: never match). XSS-safety
// comes from the DOM build path (createElement + setAttribute + textContent, never innerHTML) and the
// forced https?:// scheme; the "<" exclusion is defense-in-depth. Flags are applied per use in fillBody.
const CHAT_URL_REGEX = /https?:\/\/[^\s<]+/;

// Shows the real photo when avatar_url is present, otherwise the colored-initials fallback
// ChatSenderIdentity computed server-side (so it matches AvatarComponent elsewhere in the app).
export function fillAvatar(row, user) {
  applyAvatar(
    row.querySelector("[data-chat-message-avatar]"),
    row.querySelector("[data-chat-message-avatar-fallback]"),
    user
  );
}

// Sets the display name (a new-tab link when profileUrl is given) and, for a channel admin, appends
// the moderator badge cloned from badgeTemplate into the identity slot already in the row.
export function fillIdentity(row, name, { isChannelAdmin = false, profileUrl = null, badgeTemplate = null } = {}) {
  const nameEl = row.querySelector("[data-chat-message-name]");
  nameEl.className = `font-semibold ${isChannelAdmin ? "text-accent" : "text-strong-text"}`;

  if (profileUrl) {
    const link = document.createElement("a");
    link.setAttribute("href", profileUrl);
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener");
    link.className = "text-inherit hover:underline";
    link.textContent = name;
    nameEl.replaceChildren(link);
  } else {
    nameEl.textContent = name;
  }

  if (isChannelAdmin && badgeTemplate) row.querySelector("[data-chat-message-identity]").appendChild(buildModeratorBadge(badgeTemplate));
}

// Renders the message body. For moderators, bare https?:// URLs become new-tab links; everyone
// else's body is plain text. Anchors are built via createElement + textContent (never innerHTML),
// and only https?:// matches, so pasted javascript:/data:/HTML can never become a link.
export function fillBody(bodyEl, body, senderIsModerator) {
  const text = body || "";
  if (!senderIsModerator) {
    bodyEl.textContent = text;
    return;
  }
  bodyEl.replaceChildren();
  const regex = new RegExp(CHAT_URL_REGEX.source, "gi"); // fresh instance: global regex is stateful
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    let url = match[0];
    // Don't swallow trailing sentence punctuation, e.g. "see https://x.com." or "(https://x.com)".
    const trailing = url.match(/[.,;:!?)\]}'">]+$/);
    if (trailing) url = url.slice(0, url.length - trailing[0].length);
    const start = match.index;
    const end = start + url.length;
    if (start > lastIndex) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex, start)));
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer nofollow");
    link.className = "text-accent underline";
    link.textContent = url;
    bodyEl.appendChild(link);
    lastIndex = end;
    regex.lastIndex = end; // resume after the trimmed URL so trailing punctuation is re-emitted as text
  }
  if (lastIndex < text.length) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex)));
}

function buildModeratorBadge(template) {
  const badge = document.createElement("span");
  badge.className = "inline-flex items-center";
  badge.setAttribute("role", "img");
  badge.setAttribute("aria-label", "Moderator");
  badge.title = "Moderator";
  badge.appendChild(template.content.cloneNode(true));
  badge.querySelectorAll("svg, img").forEach((el) => el.setAttribute("aria-hidden", "true"));
  return badge;
}
